import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Trash2, Archive, Info, XCircle } from 'lucide-react';

/**
 * ConfirmDialog - A uniform confirmation dialog for the app
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog should close
 * @param {string} title - Dialog title
 * @param {string} description - Dialog description/message
 * @param {function} onConfirm - Callback when user confirms
 * @param {function} onCancel - Optional callback when user cancels
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - Visual style: "danger", "warning", "info" (default: "warning")
 * @param {boolean} destructive - If true, confirm button is red (default: false)
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = "",
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning",
  destructive = false
}) => {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const iconMap = {
    danger: <Trash2 className="w-6 h-6 text-red-500" />,
    warning: <AlertTriangle className="w-6 h-6 text-amber-500" />,
    info: <Info className="w-6 h-6 text-blue-500" />,
    archive: <Archive className="w-6 h-6 text-amber-500" />
  };

  const bgMap = {
    danger: "bg-red-50 dark:bg-red-900/20",
    warning: "bg-amber-50 dark:bg-amber-900/20",
    info: "bg-blue-50 dark:bg-blue-900/20",
    archive: "bg-amber-50 dark:bg-amber-900/20"
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${bgMap[variant]}`}>
              {iconMap[variant]}
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg font-semibold">
                {title}
              </AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="mt-2 text-sm text-muted-foreground">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel 
            onClick={handleCancel}
            className="h-10"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={`h-10 ${destructive || variant === 'danger' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'btn-primary'
            }`}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * useConfirmDialog - Hook for managing confirm dialog state
 * 
 * Usage:
 * const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
 * 
 * // In your handler:
 * const confirmed = await showConfirm({
 *   title: "Delete Item?",
 *   description: "This action cannot be undone.",
 *   variant: "danger",
 *   confirmText: "Delete"
 * });
 * if (confirmed) { ... }
 * 
 * // In your JSX:
 * {ConfirmDialogComponent}
 */
import { useState, useCallback } from 'react';

export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    description: "",
    variant: "warning",
    confirmText: "Confirm",
    cancelText: "Cancel",
    destructive: false,
    resolve: null
  });

  const showConfirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialogState({
        open: true,
        title: options.title || "Are you sure?",
        description: options.description || "",
        variant: options.variant || "warning",
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        destructive: options.destructive || false,
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    dialogState.resolve?.(true);
    setDialogState(prev => ({ ...prev, open: false }));
  }, [dialogState.resolve]);

  const handleCancel = useCallback(() => {
    dialogState.resolve?.(false);
    setDialogState(prev => ({ ...prev, open: false }));
  }, [dialogState.resolve]);

  const ConfirmDialogComponent = (
    <ConfirmDialog
      open={dialogState.open}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      title={dialogState.title}
      description={dialogState.description}
      variant={dialogState.variant}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      destructive={dialogState.destructive}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { showConfirm, ConfirmDialogComponent };
};

export default ConfirmDialog;
